import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Trophy, RotateCcw, Check, X, ChevronRight, Loader2 } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { QUIZ, LEVELS } from './quizData';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function prepareQuestions(cat, level) {
  return shuffle(QUIZ[cat][level]).map((q) => {
    const order = shuffle([0, 1, 2, 3]);
    return { q: q.q, a: order.map((i) => q.a[i]), correct: order.indexOf(q.correct) };
  });
}

export default function MobileQuiz() {
  const { me, loading } = useMobile();
  const [cat, setCat] = useState(null);
  const [level, setLevel] = useState(null);
  const [myScores, setMyScores] = useState({});

  useEffect(() => {
    if (me) mapi.get('/app/quiz/my-scores').then((r) => setMyScores(r.data)).catch(() => {});
  }, [me]);

  if (loading) return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  if (!me) return <Navigate to="/m/login" replace />;

  if (cat && level) {
    return <QuizRun cat={cat} level={level}
                    onExit={() => { setLevel(null); mapi.get('/app/quiz/my-scores').then((r) => setMyScores(r.data)).catch(() => {}); }} />;
  }

  return (
    <MobileShell>
      <MobileHeader title="Musiktheorie-Quiz" backTo={cat ? undefined : '/m/tools'} />
      {!cat ? (
        <div className="px-4 pt-4">
          <p className="text-xs mb-3" style={{ color: C.dim }}>Wähle eine Kategorie:</p>
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
            {Object.entries(QUIZ).map(([k, v]) => (
              <button key={k} onClick={() => setCat(k)} data-testid={`quiz-cat-${k}`}
                      className="w-full flex items-center gap-3 px-4 py-4 text-left active:opacity-70"
                      style={{ background: C.card, borderBottom: `1px solid ${C.border}`, color: C.text }}>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold">{v.label}</span>
                  <span className="block text-xs" style={{ color: C.dim }}>{v.desc}</span>
                </span>
                <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-4 pt-4">
          <button onClick={() => setCat(null)} className="text-xs mb-3 underline" style={{ color: C.dim }}
                  data-testid="quiz-back-to-cats">← Andere Kategorie</button>
          <h2 className="text-base font-bold mb-1">{QUIZ[cat].label}</h2>
          <p className="text-xs mb-4" style={{ color: C.dim }}>Wähle den Schwierigkeitsgrad:</p>
          <div className="space-y-2">
            {LEVELS.map((l) => {
              const best = myScores[`${cat}:${l.k}`];
              return (
                <button key={l.k} onClick={() => setLevel(l.k)} data-testid={`quiz-level-${l.k}`}
                        className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left active:opacity-70"
                        style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: l.color }} />
                  <span className="flex-1 text-sm font-semibold">{l.label}</span>
                  {best && (
                    <span className="text-xs flex items-center gap-1" style={{ color: C.dim }}>
                      <Trophy className="w-3.5 h-3.5" style={{ color: '#eab308' }} />
                      Best: {best.score}/{best.total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </MobileShell>
  );
}

function QuizRun({ cat, level, onExit }) {
  const questions = useMemo(() => prepareQuestions(cat, level), [cat, level]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const q = questions[idx];
  const levelInfo = LEVELS.find((l) => l.k === level);

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) setDone(true);
    else { setIdx(idx + 1); setPicked(null); }
  };

  if (done) return <QuizResult cat={cat} level={level} score={score} total={questions.length} onExit={onExit} />;

  return (
    <MobileShell>
      <MobileHeader title={`${QUIZ[cat].label} · ${levelInfo.label}`} backTo={undefined} />
      <div className="px-4 pt-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2 text-xs" style={{ color: C.dim }}>
          <span data-testid="quiz-progress-text">Frage {idx + 1} / {questions.length}</span>
          <span>Punkte: {score}</span>
        </div>
        <div className="h-1.5 rounded-full mb-5" style={{ background: C.card }}>
          <div className="h-1.5 rounded-full transition-all duration-300"
               style={{ background: levelInfo.color, width: `${((idx + (picked !== null ? 1 : 0)) / questions.length) * 100}%` }} />
        </div>
        <h2 className="text-base font-semibold mb-5" data-testid="quiz-question">{q.q}</h2>
        <div className="space-y-2.5">
          {q.a.map((ans, i) => {
            let bg = C.card, border = C.border;
            if (picked !== null) {
              if (i === q.correct) { bg = '#14331f'; border = '#22c55e'; }
              else if (i === picked) { bg = '#3a1d20'; border = C.red; }
            }
            return (
              <button key={i} onClick={() => pick(i)} data-testid={`quiz-answer-${i}`}
                      className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-sm transition-colors"
                      style={{ background: bg, border: `1px solid ${border}`, color: C.text }}>
                <span className="flex-1">{ans}</span>
                {picked !== null && i === q.correct && <Check className="w-4 h-4" style={{ color: '#22c55e' }} />}
                {picked !== null && i === picked && i !== q.correct && <X className="w-4 h-4" style={{ color: C.red }} />}
              </button>
            );
          })}
        </div>
        {picked !== null && (
          <button onClick={next} data-testid="quiz-next-btn"
                  className="mt-6 w-full py-3.5 rounded-xl text-sm font-bold text-white active:opacity-80"
                  style={{ background: C.red }}>
            {idx + 1 >= questions.length ? 'Ergebnis anzeigen' : 'Weiter'}
          </button>
        )}
        <button onClick={onExit} className="mt-auto mb-5 pt-4 text-xs underline self-center" style={{ color: C.dim }}
                data-testid="quiz-abort-btn">Quiz abbrechen</button>
      </div>
    </MobileShell>
  );
}

export { QuizResult };

function QuizResult({ cat, level, score, total, onExit, label }) {
  const [board, setBoard] = useState(null);
  const [newBest, setNewBest] = useState(false);
  const [saving, setSaving] = useState(true);
  const levelInfo = LEVELS.find((l) => l.k === level);
  const catLabel = label || QUIZ[cat].label;
  const submitted = useRef(false);

  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;
    let mounted = true;
    mapi.post('/app/quiz/score', { category: cat, level, score, total })
      .then((r) => { if (mounted) setNewBest(r.data.new_best); })
      .catch(() => {})
      .then(() => mapi.get('/app/quiz/leaderboard', { params: { category: cat, level } }))
      .then((r) => { if (mounted) setBoard(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setSaving(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = Math.round((score / total) * 100);
  const msg = pct === 100 ? 'Perfekt! 🎉' : pct >= 80 ? 'Stark gespielt!' : pct >= 50 ? 'Gut gemacht!' : 'Weiter üben – du schaffst das!';

  return (
    <MobileShell>
      <MobileHeader title="Ergebnis" backTo={undefined} />
      <div className="px-4 pt-6 pb-6 flex-1 flex flex-col">
        <div className="rounded-2xl px-5 py-6 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <Trophy className="w-10 h-10 mx-auto mb-2" style={{ color: '#eab308' }} />
          <div className="text-3xl font-black" data-testid="quiz-result-score">{score} / {total}</div>
          <div className="text-sm mt-1" style={{ color: C.dim }}>{catLabel} · {levelInfo.label}</div>
          <div className="text-sm font-semibold mt-2">{msg}</div>
          {newBest && (
            <div className="mt-3 inline-block text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                 style={{ background: '#14331f', color: '#22c55e' }} data-testid="quiz-new-best-badge">
              Neue persönliche Bestleistung!
            </div>
          )}
        </div>

        <div className="mt-5">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4" style={{ color: C.red }} /> Bestenliste
          </h3>
          {saving && <div className="flex items-center gap-2 text-xs py-3" style={{ color: C.dim }}><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>}
          {!saving && board && board.entries.length > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }} data-testid="quiz-leaderboard">
              {board.entries.map((e) => (
                <div key={e.rank} className="flex items-center gap-3 px-4 py-2.5 text-sm"
                     style={{ background: e.is_me ? '#2a1e20' : C.card, borderBottom: `1px solid ${C.border}`,
                              color: C.text }}>
                  <span className="w-6 text-center font-bold"
                        style={{ color: e.rank === 1 ? '#eab308' : e.rank === 2 ? '#c0c0c8' : e.rank === 3 ? '#cd7f32' : C.dim }}>
                    {e.rank}
                  </span>
                  <span className="flex-1 truncate">{e.name}{e.is_me ? ' (Du)' : ''}</span>
                  <span className="font-semibold">{e.score}/{e.total}</span>
                </div>
              ))}
              {board.me && board.me.rank > 10 && (
                <div className="flex items-center gap-3 px-4 py-2.5 text-sm" style={{ background: '#2a1e20', color: C.text }}>
                  <span className="w-6 text-center font-bold" style={{ color: C.dim }}>{board.me.rank}</span>
                  <span className="flex-1 truncate">{board.me.name} (Du)</span>
                  <span className="font-semibold">{board.me.score}/{board.me.total}</span>
                </div>
              )}
            </div>
          )}
          {!saving && (!board || !board.entries.length) && (
            <p className="text-xs" style={{ color: C.dim }}>Noch keine Einträge.</p>
          )}
        </div>

        <div className="mt-auto pt-6 space-y-2">
          <button onClick={onExit} data-testid="quiz-play-again-btn"
                  className="w-full py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 active:opacity-80"
                  style={{ background: C.red }}>
            <RotateCcw className="w-4 h-4" /> Nochmal spielen
          </button>
        </div>
      </div>
    </MobileShell>
  );
}
